import { useState, useEffect } from "react";
import { getCollaborateurs, getBibliothequeDroits, updateBibliothequeDroits } from "../services/api";
import ModaleV2 from "./ModaleV2";

// ===================================================================
// The Crowd v2 — Bibliotheques : droits d'acces d'une bibliotheque
//
// Reservee aux directeurs (le backend le verifie aussi). Les directeurs
// ont toujours un acces complet et ne figurent pas dans la liste. Pour
// chaque autre collaborateur : Aucun acces, Lecture ou Full, prerempli
// avec les droits actuels. L'enregistrement remplace la liste complete,
// en une transaction (PUT /api/bibliotheques/{slug}/droits).
//
// Props : bibliotheque ({ slug, nom }), collaborateurId, onEnregistre(nb), onClose
// ===================================================================

export default function ModaleDroitsBibliothequeV2({ bibliotheque, collaborateurId, onEnregistre, onClose }) {
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [niveaux, setNiveaux] = useState({}); // { collaborateur_id: "lecture" | "full" }
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    let annule = false;
    Promise.all([getCollaborateurs(), getBibliothequeDroits(bibliotheque.slug, collaborateurId)])
      .then(([liste, droits]) => {
        if (annule) return;
        setCollaborateurs(liste.filter((c) => c.role !== "directeur"));
        const initiaux = {};
        droits.forEach((d) => { initiaux[d.collaborateur_id] = d.niveau; });
        setNiveaux(initiaux);
      })
      .catch((err) => { if (!annule) setErreur(`Droits non chargés : ${err.message}`); })
      .finally(() => { if (!annule) setChargement(false); });
    return () => { annule = true; };
  }, [bibliotheque.slug, collaborateurId]);

  const changer = (id, niveau) =>
    setNiveaux((n) => {
      const suivant = { ...n };
      if (niveau) suivant[id] = niveau;
      else delete suivant[id];
      return suivant;
    });

  const enregistrer = async () => {
    setEnCours(true);
    setErreur(null);
    const droits = Object.entries(niveaux).map(([id, niveau]) => ({ collaborateur_id: Number(id), niveau }));
    try {
      await updateBibliothequeDroits(bibliotheque.slug, droits, collaborateurId);
      onEnregistre(droits.length);
    } catch (err) {
      setErreur(err.message);
      setEnCours(false);
    }
  };

  const nbAcces = Object.keys(niveaux).length;

  return (
    <ModaleV2
      titre={`Droits d'accès — ${bibliotheque.nom}`}
      onClose={onClose}
      bloquee={enCours}
      actions={
        <>
          <button type="button" className="v2-btn v2-btn-secondaire" onClick={onClose} disabled={enCours}>Annuler</button>
          <button type="button" className="v2-btn v2-btn-principal" onClick={enregistrer} disabled={enCours || chargement}>
            {enCours ? "Enregistrement…" : "Enregistrer"}
          </button>
        </>
      }
    >
      {erreur && <div className="v2-msg v2-msg-erreur">{erreur}</div>}
      <p className="v2-champ-aide">
        Les directeurs ont toujours un accès complet. {nbAcces} autre{nbAcces > 1 ? "s" : ""} personne{nbAcces > 1 ? "s ont" : " a"} accès à cette bibliothèque.
      </p>

      {chargement ? (
        <div className="v2-chargement">Chargement…</div>
      ) : (
        <div className="v2-tableau-conteneur v2-bib-droits-tableau">
          <table className="v2-tableau">
            <thead>
              <tr>
                <th scope="col">Collaborateur</th>
                <th scope="col">Profil</th>
                <th scope="col">Accès</th>
              </tr>
            </thead>
            <tbody>
              {collaborateurs.map((c) => (
                <tr key={c.id}>
                  <td>{c.prenom} {c.nom}</td>
                  <td className="v2-cellule-discrete">{c.profil || c.role}</td>
                  <td>
                    <div className="v2-champ">
                      <label className="v2-invisible" htmlFor={`v2-droit-bib-${c.id}`}>Accès de {c.prenom} {c.nom}</label>
                      <select id={`v2-droit-bib-${c.id}`} value={niveaux[c.id] || ""} onChange={(e) => changer(c.id, e.target.value)} disabled={enCours}>
                        <option value="">Aucun accès</option>
                        <option value="lecture">Lecture</option>
                        <option value="full">Full</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ModaleV2>
  );
}
